import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ButtonGroup from '@mui/material/ButtonGroup';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import LinearProgress from '@mui/material/LinearProgress';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { zodResolver } from '@hookform/resolvers/zod';
import { FC, useEffect } from 'react';
import { DevTool } from '@hookform/devtools';
import { useForm, Controller } from 'react-hook-form';
import {
  MdShield,
  MdPublic,
  MdLock,
  MdWork,
  MdFacebook,
  MdLocationOn,
  MdAccessTime,
  MdEmail,
  MdLanguage,
} from 'react-icons/md';
import { IoIosHelpCircleOutline } from 'react-icons/io';
import { SiInstagram, SiYoutube, SiX, SiLinkedin, SiTiktok } from 'react-icons/si';
import { TfiEmail } from 'react-icons/tfi';
import { useUpdateProfileMutation } from '@onesource/services';
import { EImageVariant, TUpdateProfile, ProfileFormSchema, TProfileFormValues } from '@onesource/schemas';
import {
  buildProcessHandler,
  FILE_FORMATS,
  FormProvider,
  RHFCountry,
  RHFLanguage,
  RHFPhoneInput,
  RHFSubmitBtn,
  RHFTextField,
  RHFTimezone,
  RhfMediaInlineUpload,
} from '@onesource/components';
import { SHOW_DEV_TOOLS } from 'src/config';

// ----------------------------------------------------------------------

const avatarProcessHandler = buildProcessHandler([EImageVariant.w400, EImageVariant.original]);

const EMPTY_ADDRESS = { city: '', state: '', country: '' };
const EMPTY_SOCIALS = { facebook: '', instagram: '', youtube: '', twitter: '', linkedin: '', tiktok: '' };

// Marks fields that are not required by ProfileFormSchema, mirroring the
// red `*` used on required field labels.
const OptionalChip: FC = () => (
  <Chip
    label="OPTIONAL"
    size="small"
    variant="outlined"
    sx={{ height: 20, fontSize: 11, fontWeight: 500, color: 'text.secondary', borderColor: 'divider' }}
  />
);

export type TProfileTabAddress = {
  city?: string | null;
  state?: string | null;
  country?: string | null;
};

export type TProfileTabSocials = {
  facebook?: string | null;
  instagram?: string | null;
  youtube?: string | null;
  twitter?: string | null;
  linkedin?: string | null;
  tiktok?: string | null;
};

export type TProfileTabProps = {
  firstName?: string;
  lastName?: string;
  email?: string;
  avatar?: string | null;
  displayName?: string;
  username?: string | null;
  timezone?: string | null;
  occupation?: string | null;
  bio?: string | null;
  primaryLanguage?: string | null;
  phoneNumber?: string | null;
  zoomEmail?: string | null;
  address?: TProfileTabAddress | null;
  socials?: TProfileTabSocials | null;
  isPrivate?: boolean;
};

export const ProfileTab: FC<TProfileTabProps> = props => {
  const {
    firstName,
    lastName,
    email,
    avatar,
    displayName,
    username,
    timezone,
    occupation,
    bio,
    primaryLanguage,
    phoneNumber,
    zoomEmail,
    address,
    socials,
    isPrivate,
  } = props;

  const { mutateAsync, isPending } = useUpdateProfileMutation();

  const methods = useForm<TProfileFormValues>({
    resolver: zodResolver(ProfileFormSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      displayName: '',
      occupation: '',
      timezone: '',
      isPrivate: isPrivate ?? false,
      avatar: '',
      bio: '',
      primaryLanguage: '',
      phoneNumber: '',
      zoomEmail: '',
      address: EMPTY_ADDRESS,
      socials: EMPTY_SOCIALS,
    },
    mode: 'all',
  });

  const {
    handleSubmit,
    control,
    reset,
    formState: { isDirty },
  } = methods;

  useEffect(() => {
    reset({
      firstName: firstName ?? '',
      lastName: lastName ?? '',
      email: email ?? '',
      displayName: displayName ?? '',
      occupation: occupation ?? '',
      timezone: timezone ?? '',
      isPrivate: isPrivate ?? false,
      avatar: avatar ?? '',
      bio: bio ?? '',
      primaryLanguage: primaryLanguage ?? '',
      phoneNumber: phoneNumber ?? '',
      zoomEmail: zoomEmail ?? '',
      address: {
        city: address?.city ?? '',
        state: address?.state ?? '',
        country: address?.country ?? '',
      },
      socials: {
        facebook: socials?.facebook ?? '',
        instagram: socials?.instagram ?? '',
        youtube: socials?.youtube ?? '',
        twitter: socials?.twitter ?? '',
        linkedin: socials?.linkedin ?? '',
        tiktok: socials?.tiktok ?? '',
      },
    });
  }, [
    firstName,
    lastName,
    email,
    displayName,
    timezone,
    occupation,
    avatar,
    isPrivate,
    bio,
    primaryLanguage,
    phoneNumber,
    zoomEmail,
    address,
    socials,
    reset,
  ]);

  const onSubmit = handleSubmit(async data => {
    const payload: TUpdateProfile & { avatar?: string } = {
      firstName: data.firstName,
      lastName: data.lastName,
      email: data.email,
      displayName: data.displayName,
      occupation: data.occupation,
      timezone: data.timezone,
      isPrivate: data.isPrivate,
      avatar: data.avatar,
      bio: data.bio,
      primaryLanguage: data.primaryLanguage,
      phoneNumber: data.phoneNumber,
      zoomEmail: data.zoomEmail,
      address: data.address,
      socials: data.socials,
    };
    await mutateAsync(payload);
  });

  return (
    <Box sx={{ py: 0 }}>
      <FormProvider methods={methods} onSubmit={onSubmit}>
        <LinearProgress sx={{ mb: 2, borderRadius: 1, visibility: isPending ? 'visible' : 'hidden' }} />
        <Grid container spacing={3}>
          {/* Avatar upload */}
          <Grid item xs={12} sm="auto">
            <Stack spacing={2} alignItems="center">
              <Controller
                name="avatar"
                control={control}
                render={({ field, fieldState }) => (
                  <RhfMediaInlineUpload
                    value={field.value}
                    onChange={field.onChange}
                    label="Avatar image"
                    processHandler={avatarProcessHandler}
                    allowedFormats={FILE_FORMATS.IMAGE}
                    selectVariant={EImageVariant.w400}
                    uploadVariants={[EImageVariant.w400]}
                    cropAspectRatio={1}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                  />
                )}
              />

              {username && (
                <Typography variant="body2" component="div" sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  @{username}{' '}
                  <Tooltip title="Your username cannot be changed. If you need to update it, please contact support.">
                    <Box sx={{ display: 'flex', cursor: 'pointer', color: 'text.secondary' }}>
                      <IoIosHelpCircleOutline size={20} />
                    </Box>
                  </Tooltip>
                </Typography>
              )}
            </Stack>
          </Grid>

          <Grid item xs={12} sm>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  First Name{' '}
                  <Box component="span" sx={{ color: 'error.main' }}>
                    *
                  </Box>
                </Typography>
                <RHFTextField name="firstName" placeholder="First Name" disabled={isPending} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Last Name{' '}
                  <Box component="span" sx={{ color: 'error.main' }}>
                    *
                  </Box>
                </Typography>
                <RHFTextField name="lastName" placeholder="Last Name" disabled={isPending} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Display Name{' '}
                  <Box component="span" sx={{ color: 'error.main' }}>
                    *
                  </Box>
                </Typography>
                <RHFTextField name="displayName" placeholder="Display Name" disabled={isPending} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    height: '100%',
                  }}
                >
                  <Box>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        mb: 1.5,
                        color: 'success.main',
                      }}
                    >
                      <MdShield size={18} />
                      <Typography variant="subtitle2" color="success.main">
                        Privacy Mode
                      </Typography>
                    </Box>

                    <Controller
                      name="isPrivate"
                      control={control}
                      render={({ field }) => (
                        <Stack direction="row" alignItems="center" spacing={2}>
                          <ButtonGroup
                            variant="outlined"
                            size="small"
                            sx={{
                              mb: 0.5,
                              '& .MuiButton-root': { px: 2 },
                            }}
                          >
                            <Button
                              onClick={() => field.onChange(false)}
                              variant={field.value ? 'outlined' : 'contained'}
                              color={field.value ? 'inherit' : 'primary'}
                              startIcon={<MdPublic size={16} />}
                              disabled={isPending}
                            >
                              Public
                            </Button>
                            <Button
                              onClick={() => field.onChange(true)}
                              variant={field.value ? 'contained' : 'outlined'}
                              color={field.value ? 'error' : 'inherit'}
                              startIcon={<MdLock size={16} />}
                              disabled={isPending}
                            >
                              Private
                            </Button>
                          </ButtonGroup>
                          <Tooltip title="When set to Private, you won't be visible in leaderboards, @mentions, or online presence features.">
                            <Box sx={{ display: 'flex', cursor: 'pointer', color: 'text.secondary' }}>
                              <IoIosHelpCircleOutline size={30} />
                            </Box>
                          </Tooltip>
                        </Stack>
                      )}
                    />
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        <Divider sx={{ my: 4 }} />

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
            gap: 3,
          }}
        >
          {/* Bio (full row) */}
          <Box sx={{ gridColumn: { sm: '1 / -1' } }}>
            <Typography
              variant="subtitle2"
              component="div"
              sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.75 }}
            >
              Bio
              <OptionalChip />
            </Typography>
            <RHFTextField
              name="bio"
              placeholder="A short intro visible on your profile (up to 300 characters)."
              multiline
              minRows={3}
              disabled={isPending}
              helperText="Share what brings you here or your experience."
            />
          </Box>

          {/* Email / Occupation */}
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <TfiEmail /> Email{' '}
              <Box component="span" sx={{ color: 'error.main' }}>
                *
              </Box>
            </Typography>
            <RHFTextField
              name="email"
              helperText="Primary account email for login, notifications, and support."
              placeholder="Email Address"
              disabled={isPending}
            />
          </Box>

          <Box>
            <Typography
              variant="subtitle2"
              component="div"
              sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.75 }}
            >
              <MdEmail size={18} />
              Zoom Email
              <OptionalChip />
            </Typography>
            <RHFTextField
              name="zoomEmail"
              helperText="You will be required to login to YOUR Zoom account prior to joining the lives calls. If this is not correct, you will not be able to join."
              placeholder="Zoom Email"
              disabled={isPending}
            />
          </Box>

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <MdWork size={18} />
              Profession{' '}
              <Box component="span" sx={{ color: 'error.main' }}>
                *
              </Box>
            </Typography>
            <RHFTextField name="occupation" placeholder="Your current role or profession." disabled={isPending} />
          </Box>

          {/* Primary language / Zoom email */}
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <MdLanguage size={18} />
              Primary Language{' '}
              <Box component="span" sx={{ color: 'error.main' }}>
                *
              </Box>
            </Typography>
            <RHFLanguage name="primaryLanguage" placeholder="Primary Language" disabled={isPending} />
          </Box>

          {/* Phone Number (full row) */}
          <Box sx={{ gridColumn: { sm: '1 / -1' } }}>
            <Typography
              variant="subtitle2"
              component="div"
              sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.75 }}
            >
              Phone Number
              <OptionalChip />
            </Typography>
            <RHFPhoneInput
              name="phoneNumber"
              placeholder="Phone Number"
              helperText="For verification or urgent support only — never shared publicly."
              disabled={isPending}
            />
          </Box>

          {/* Address: Street + City / State / Zip / Country (full row) */}
          <Box sx={{ gridColumn: { sm: '1 / -1' } }}>
            <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <MdLocationOn size={18} />
              Location{' '}
              <Box component="span" sx={{ color: 'error.main' }}>
                *
              </Box>
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                gap: 2,
              }}
            >
              <RHFTextField name="address.city" placeholder="City" disabled={isPending} />
              <RHFTextField name="address.state" placeholder="State" disabled={isPending} />
              <RHFCountry name="address.country" placeholder="Country" disabled={isPending} />
            </Box>
          </Box>

          {/* Timezone (full row) */}
          <Box sx={{ gridColumn: { sm: '1 / -1' } }}>
            <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <MdAccessTime size={18} />
              Timezone{' '}
              <Box component="span" sx={{ color: 'error.main' }}>
                *
              </Box>
            </Typography>
            <RHFTimezone name="timezone" placeholder="Time Zone" disabled={isPending} />
          </Box>

          {/* Social Networks (full row) */}
          <Box sx={{ gridColumn: { sm: '1 / -1' } }}>
            <Typography
              variant="subtitle2"
              component="div"
              sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.75 }}
            >
              Social Networks
              <OptionalChip />
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                gap: 2,
              }}
            >
              <RHFTextField
                name="socials.facebook"
                placeholder="https://facebook.com/yourprofile"
                disabled={isPending}
                InputProps={{
                  startAdornment: <MdFacebook size={20} style={{ marginRight: 8, color: '#1877F2', flexShrink: 0 }} />,
                }}
              />
              <RHFTextField
                name="socials.instagram"
                placeholder="https://instagram.com/yourprofile"
                disabled={isPending}
                InputProps={{
                  startAdornment: <SiInstagram size={20} style={{ marginRight: 8, color: '#E1306C', flexShrink: 0 }} />,
                }}
              />
              <RHFTextField
                name="socials.youtube"
                placeholder="https://youtube.com/@yourchannel"
                disabled={isPending}
                InputProps={{
                  startAdornment: <SiYoutube size={20} style={{ marginRight: 8, color: '#FF0000', flexShrink: 0 }} />,
                }}
              />
              <RHFTextField
                name="socials.twitter"
                placeholder="https://x.com/yourhandle"
                disabled={isPending}
                InputProps={{
                  startAdornment: <SiX size={20} style={{ marginRight: 8, flexShrink: 0 }} />,
                }}
              />
              <RHFTextField
                name="socials.linkedin"
                placeholder="https://linkedin.com/in/yourprofile"
                disabled={isPending}
                InputProps={{
                  startAdornment: <SiLinkedin size={20} style={{ marginRight: 8, color: '#0A66C2', flexShrink: 0 }} />,
                }}
              />
              <RHFTextField
                name="socials.tiktok"
                placeholder="https://tiktok.com/@yourhandle"
                disabled={isPending}
                InputProps={{
                  startAdornment: <SiTiktok size={20} style={{ marginRight: 8, flexShrink: 0 }} />,
                }}
              />
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            pt: 3,
            mt: 2,
            borderTop: theme => `1px solid ${theme.palette.divider}`,
          }}
        >
          <RHFSubmitBtn buttonProps={{ disabled: isPending || !isDirty }}>
            {isPending ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={16} color="inherit" />
                Saving...
              </Box>
            ) : (
              'Save Changes'
            )}
          </RHFSubmitBtn>
        </Box>
        {SHOW_DEV_TOOLS && <DevTool id="account-profile" control={methods.control} />}
      </FormProvider>
    </Box>
  );
};
